/**
 * MessagesDropdown
 *
 * A dropdown menu in the navbar that displays recent messages
 * with quick access to conversations and unread count badge.
 *
 * Follows the pattern established by PendingConnectionsDropdown.tsx
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Clock,
  User,
  AlertCircle,
  Loader2,
  ChevronRight,
  Mail,
  Send,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  useConversations,
  useUnreadCount,
  useMessagingLoading,
  useMessagingError,
  useMessagingActions,
} from '@/stores/messaging';
import { NewConversationModal } from '@/components/messages/NewConversationModal';
import type { Conversation } from '@/types/messaging';

// Maximum items to show in dropdown
const MAX_DROPDOWN_ITEMS = 5;

interface MessagesDropdownProps {
  className?: string;
}

/**
 * Get initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

const getGroupInitials = (name?: string): string => {
  if (!name) return 'G';

  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
  }

  return words[0]?.charAt(0).toUpperCase() || 'G';
};

/**
 * Get display metadata for a conversation
 */
const getConversationDisplay = (conversation: Conversation) => {
  if (conversation.isGroup) {
    const participantNames = conversation.participants
      ?.map((participant) => {
        if (participant.firstName && participant.lastName) {
          return `${participant.firstName} ${participant.lastName}`;
        }
        return participant.firstName || participant.username;
      })
      .filter(Boolean);

    const displayName = conversation.groupName || participantNames?.join(', ') || 'Group conversation';

    return {
      displayName,
      initials: getGroupInitials(displayName),
      avatar: conversation.groupAvatar,
      subtitle: `${conversation.participants?.length ?? 0} member${conversation.participants?.length === 1 ? '' : 's'}`,
      isOnline: false,
    };
  }

  const { participant } = conversation;
  if (!participant) {
    return {
      displayName: 'Unknown',
      initials: getInitials(),
      avatar: undefined,
      subtitle: '',
      isOnline: false,
    };
  }

  let displayName: string;
  if (participant.firstName && participant.lastName) {
    displayName = `${participant.firstName} ${participant.lastName}`;
  } else if (participant.firstName) {
    displayName = participant.firstName;
  } else {
    displayName = participant.username;
  }

  return {
    displayName,
    initials: getInitials(participant.firstName, participant.lastName, participant.username),
    avatar: participant.avatar,
    subtitle: participant.username ? `@${participant.username}` : '',
    isOnline: Boolean(participant.isOnline),
  };
};

/**
 * Format message timestamp
 */
const formatMessageTime = (dateString?: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) {
    return 'Just now';
  } else if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
};

/**
 * Truncate message preview
 */
const truncateMessage = (content?: string, maxLength: number = 50): string => {
  if (!content) return 'No messages yet';
  if (content.length <= maxLength) return content;
  return content.substring(0, maxLength).trim() + '...';
};

/**
 * MessagesDropdown
 *
 * Navbar dropdown showing recent messages with quick access.
 */
export const MessagesDropdown: React.FC<MessagesDropdownProps> = ({
  className,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);

  const conversations = useConversations();
  const totalUnreadCount = useUnreadCount();
  const { isLoadingConversations } = useMessagingLoading();
  const error = useMessagingError();
  const {
    fetchConversations,
    fetchUnreadCount,
    clearError,
  } = useMessagingActions();

  // Fetch conversations when dropdown opens
  useEffect(() => {
    if (open) {
      fetchConversations(true);
    }
  }, [open, fetchConversations]);

  // Fetch unread count on mount
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Navigate to conversation
  const handleConversationClick = (conversationId: string) => {
    navigate(`/dashboard/messages?conversation=${conversationId}`);
    setOpen(false);
  };

  // Handle new message
  const handleNewMessage = () => {
    setShowNewMessageModal(true);
    setOpen(false);
  };

  // Display count for badge
  const displayCount = totalUnreadCount > 99 ? '99+' : totalUnreadCount;

  // Get recent non-archived conversations
  const recentConversations = conversations
    .filter((c) => !c.archivedAt)
    .slice(0, MAX_DROPDOWN_ITEMS);

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              'relative p-2 hover:bg-accent rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
              className
            )}
            aria-label={
              totalUnreadCount > 0
                ? `${totalUnreadCount} unread message${totalUnreadCount !== 1 ? 's' : ''}`
                : 'No unread messages'
            }
          >
            <MessageSquare className="h-5 w-5" aria-hidden="true" />

            {/* Notification badge */}
            {totalUnreadCount > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white animate-in zoom-in-50 duration-200"
                style={{ backgroundColor: 'hsl(var(--primary))' }}
                aria-hidden="true"
              >
                {displayCount}
              </span>
            )}
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          className="w-[380px] max-h-[520px] overflow-hidden"
          sideOffset={8}
        >
          {/* Header */}
          <DropdownMenuLabel className="flex items-center justify-between py-3 px-4">
            <span className="text-base font-semibold">Messages</span>
            {totalUnreadCount > 0 && (
              <Badge
                variant="secondary"
                className="text-xs"
                style={{
                  backgroundColor: 'hsl(var(--primary) / 0.1)',
                  color: 'hsl(var(--primary))',
                }}
              >
                {totalUnreadCount} unread
              </Badge>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="my-0" />

          {/* Content */}
          <div className="overflow-y-auto max-h-[380px]">
            {/* Loading state */}
            {isLoadingConversations && conversations.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">
                  Loading messages...
                </span>
              </div>
            )}

            {/* Error state */}
            {error && !isLoadingConversations && (
              <div className="flex flex-col items-center justify-center py-8 px-4">
                <AlertCircle className="h-8 w-8 mb-2 text-destructive" />
                <p className="text-sm text-center text-muted-foreground mb-3">
                  {error}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    clearError();
                    fetchConversations(true);
                  }}
                >
                  Try Again
                </Button>
              </div>
            )}

            {/* Empty state */}
            {!isLoadingConversations && !error && recentConversations.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 px-4">
                <div
                  className="h-12 w-12 rounded-full mb-3 flex items-center justify-center"
                  style={{ backgroundColor: 'hsl(var(--primary) / 0.1)' }}
                >
                  <Mail className="h-6 w-6" style={{ color: 'hsl(var(--primary))' }} />
                </div>
                <p className="text-sm font-medium text-center mb-1">
                  No messages yet
                </p>
                <p className="text-xs text-muted-foreground text-center mb-4">
                  Start a conversation with one of your connections
                </p>
                <Button
                  size="sm"
                  onClick={handleNewMessage}
                  style={{
                    backgroundColor: 'hsl(var(--primary))',
                    color: 'hsl(var(--primary-foreground))',
                  }}
                >
                  <Send className="h-4 w-4 mr-2" />
                  New Message
                </Button>
              </div>
            )}

            {/* Conversations list */}
            {!error && recentConversations.length > 0 && (
              <div className="divide-y divide-border">
                {recentConversations.map((conversation) => (
                  <ConversationCard
                    key={conversation.id}
                    conversation={conversation}
                    onClick={() => handleConversationClick(conversation.id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <DropdownMenuSeparator className="my-0" />
          <div className="p-2 flex gap-2">
            <Button
              variant="ghost"
              className="flex-1 justify-center text-sm"
              onClick={handleNewMessage}
            >
              <Send className="h-4 w-4 mr-2" />
              New Message
            </Button>
            <Button
              variant="ghost"
              className="flex-1 justify-between text-sm"
              onClick={() => {
                navigate('/dashboard/messages');
                setOpen(false);
              }}
            >
              <span>View All</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* New Conversation Modal */}
      <NewConversationModal
        open={showNewMessageModal}
        onOpenChange={setShowNewMessageModal}
      />
    </>
  );
};

/**
 * ConversationCard
 *
 * Individual conversation item within the dropdown.
 */
interface ConversationCardProps {
  conversation: Conversation;
  onClick: () => void;
}

const ConversationCard: React.FC<ConversationCardProps> = ({
  conversation,
  onClick,
}) => {
  const { displayName, initials, avatar, subtitle, isOnline } = getConversationDisplay(conversation);
  const hasUnread = conversation.unreadCount > 0;

  return (
    <button
      className={cn(
        'w-full p-4 hover:bg-accent/50 transition-colors text-left',
        hasUnread && 'bg-accent/30'
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <Avatar className="h-10 w-10">
            <AvatarImage src={avatar} alt={displayName} />
            <AvatarFallback
              style={{
                backgroundColor: 'hsl(var(--primary) / 0.1)',
                color: 'hsl(var(--primary))',
              }}
              className="text-sm font-medium"
            >
              {initials}
            </AvatarFallback>
          </Avatar>
          {/* Online indicator */}
          {isOnline && (
            <span
              className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-card"
              style={{ backgroundColor: 'hsl(var(--primary))' }}
              aria-label="Online"
            />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <h4 className={cn(
              'text-sm truncate',
              hasUnread ? 'font-semibold' : 'font-medium'
            )}>
              {displayName}
            </h4>
            <div className="flex items-center gap-2 flex-shrink-0">
              {hasUnread && (
                <Badge
                  className="h-5 min-w-[20px] px-1.5 text-xs"
                  style={{
                    backgroundColor: 'hsl(var(--primary))',
                    color: 'hsl(var(--primary-foreground))',
                  }}
                >
                  {conversation.unreadCount}
                </Badge>
              )}
            </div>
          </div>

          {/* Username */}
          <div
            className="flex items-center gap-1 text-xs mb-1"
            style={{ color: 'hsl(var(--muted-foreground))' }}
          >
            <User className="h-3 w-3" />
            <span>{subtitle}</span>
          </div>

          {/* Message preview and time */}
          <div className="flex items-center justify-between gap-2">
            <p className={cn(
              'text-xs truncate flex-1',
              hasUnread ? 'text-foreground' : 'text-muted-foreground'
            )}>
              {truncateMessage(conversation.lastMessage?.content)}
            </p>
            {conversation.lastMessageAt && (
              <span className="text-xs text-muted-foreground flex items-center gap-1 flex-shrink-0">
                <Clock className="h-3 w-3" />
                {formatMessageTime(conversation.lastMessageAt)}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

export default MessagesDropdown;
